import React from 'react';
import PropTypes from 'prop-types';

import Button from '$partials/ui/button';
import Image from '$partials/media/image';
import { IconChevronSprite } from '$partials/icons/index.js';
import { useLocale } from '$contexts/locale.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 */
const ModuleFeature = ({
    ctaLabel,
    ctaLink,
    displayHeroImageWhole,
    eyebrowIcon,
    eyebrowTitle,
    image,
    imageMd,
    parent,
    title
}) => {
    const { formatURI } = useLocale();

    const desktopImage = imageMd || image;
    const mobileImage = image || imageMd;

    return (
        <div className={`bg-white relative ${parent === 'Home' ? 'py-40 md:py-50' : parent === 'Detail' ? 'py-20 md:py-30' : 'py-40 md:py-60'}`}>
            <div className='layout-container'>

                <div className='bg-white px-10 sm:px-5 md:px-10'>
                    <div className='flex flex-col align-baseline justify-between'>
                        <div className='flex flex-col w-full'>
                            <div className='flex items-center'>
                                {eyebrowIcon && (
                                    <div
                                        className='mr-5'
                                        style={{
                                            fontSize: '18px',
                                            width: `${(eyebrowIcon?.width / eyebrowIcon?.height)}em`,
                                            maxWidth: '100%'
                                        }}>
                                        <Image
                                            {...eyebrowIcon}
                                            loading={''} />
                                    </div>
                                )}
                                <p className='inline-block type-style-7 text-orange'>
                                    {eyebrowTitle}
                                </p>
                            </div>
                            <div className='flex flex-wrap flex-col md:flex-row justify-between md:flex-nowrap w-full md:items-center mt-18'>
                                <p className='type-style-3 mr-10 sm:w-10/12 md:w-7/12'>
                                    {title}
                                </p>
                                {ctaLabel && ctaLink && (
                                    <div className='mt-16'>
                                        <Button
                                            as='a'
                                            className='button button--secondary-alt'
                                            href={formatURI(ctaLink)}
                                            type='button'>
                                            <span>{ctaLabel}</span>
                                            <span className='button__icon button__icon--animate'>
                                                <IconChevronSprite />
                                            </span>
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>
                    {/* Desktop */}
                    {desktopImage && (displayHeroImageWhole || desktopImage?.contentType?.includes('video/')) ? (
                        <figure className='aspect-[5/2] mt-40 sm:mt-[42px] md:mt-40 hidden sm:block'>
                            <div
                                className='h-full w-full'>
                                {desktopImage?.contentType?.includes('video/') ?
                                    (
                                        <video
                                            autoPlay
                                            loop
                                            muted
                                            playsInline
                                            className='w-full h-full object-cover'
                                            src={desktopImage?.src}
                                            width={'100%'} />
                                    ) : (
                                        <Image
                                            {...desktopImage}
                                            className='w-full h-full object-cover'
                                            containerClassName='w-full h-full relative'
                                            height='100%'
                                            loadWidth={1920}
                                            width='100%' />
                                    )}
                            </div>
                        </figure>
                    ) : (
                        <figure className='mt-40 sm:mt-[42px] md:mt-40'>
                            {/* Desktop image */}
                            <div
                                className='min-h-[200px] hidden sm:block sm:min-h-[320px] md:min-h-[430px] w-full bg-contain bg-repeat-x'
                                style={{ backgroundImage: `url(${desktopImage.src})` }}
                                title={desktopImage.title} />
                        </figure>
                    )}

                    {/* Mobile */}
                    {mobileImage && (displayHeroImageWhole || mobileImage?.contentType?.includes('video/')) ? (
                        <div
                            className='h-full w-full mt-40 sm:mt-[42px] md:mt-40 sm:hidden'>
                            {mobileImage?.contentType?.includes('video/') ?
                                (
                                    <video
                                        autoPlay
                                        loop
                                        muted
                                        playsInline
                                        className='w-full h-full object-cover'
                                        src={mobileImage?.src}
                                        width={'100%'} />
                                ) : (
                                    <Image
                                        {...mobileImage}
                                        className='w-full h-full object-cover'
                                        containerClassName='w-full h-full relative'
                                        height='100%'
                                        loadWidth={1162}
                                        width='100%' />
                                )}
                        </div>
                    ) : (
                        <figure className='mt-40 sm:mt-[42px] md:mt-40'>
                            {/* Mobile image */}
                            <div
                                className='min-h-[200px] sm:hidden w-full bg-contain bg-repeat-x'
                                style={{ backgroundImage: `url(${mobileImage.src})` }}
                                title={mobileImage.title} />
                        </figure>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {
    ctaLabel: PropTypes.string,
    ctaLink: PropTypes.string,
    displayHeroImageWhole: PropTypes.bool,
    eyebrowIcon: PropTypes.object,
    eyebrowTitle: PropTypes.string,
    image: PropTypes.object,
    imageMd: PropTypes.object,
    parent: PropTypes.string,
    title: PropTypes.string,
};

ModuleFeature.propTypes = propTypes;

export default ModuleFeature;
